import { xbLoggerFactory, XbLogger } from '@amzn/xb-lite';
import { config } from '../config/app-config';

/**
 * Experience Bridge application name. MUST stay in sync (case-sensitive) with
 * the `xbAppName` entry registered in the XB XPS registry
 * (`xb-lite-applications.json`) and the value leadership sees on the global
 * `XbLite-AWSJam` dashboard.
 */
export const XB_APP_NAME = 'AWSJam';

type XbEnvironment = 'local' | 'beta' | 'gamma' | 'prod';

/**
 * Maps the app's deployment stage to the xb-lite environment, which controls
 * logger routing only (`local` = console logger; `beta`/`gamma`/`prod` = the
 * CloudWatch RUM logger reading `window.cwr`). It is not emitted as a metric
 * dimension. `alpha` and any unknown stage route through the RUM logger.
 */
const resolveXbEnvironment = (): XbEnvironment => {
  switch (config.stageName?.toLowerCase().trim()) {
    case 'prod':
      return 'prod';
    case 'gamma':
      return 'gamma';
    case 'local':
      return 'local';
    case 'beta':
    case 'alpha':
    default:
      return 'beta';
  }
};

/**
 * Creates an xb-lite logger scoped to a specific page/component.
 *
 * Emits at the component attribution level (`XbAppName` + `XpsName` +
 * `ComponentName`) because the global XB dashboard's availability and error
 * widgets query metrics at that dimension level. Each page/component should
 * pass its own `componentName` so its metrics are attributed distinctly and
 * roll up under the `AWSJam` app.
 */
export const createXbLogger = (componentName: string): XbLogger =>
  xbLoggerFactory({
    environment: resolveXbEnvironment(),
    attribution: {
      appName: XB_APP_NAME,
      serviceName: XB_APP_NAME,
      componentName,
    },
  });
